import { useEffect, useMemo, useRef, useState } from 'react';
import {
  clamp,
  type Extent,
  fitView,
  type Point,
  pan,
  pinch,
  swapImage,
  toggle,
  type ZoomFrame,
  type ZoomView,
  zoomAt,
  zoomPercent,
} from '@/lib/zoomPan';

const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 24;
const WHEEL_RATE = 0.002;
// A touch double tap can also fire dblclick; one gesture toggles once.
const DBLCLICK_AFTER_TAP_MS = 500;
// Firefox scrolls 3 lines a notch where Chromium sends about 100 px; a notch zooms alike in both.
const WHEEL_LINE_PX = 33;

function wheelUnitPx(deltaMode: number, stageHeight: number): number {
  if (deltaMode === WheelEvent.DOM_DELTA_LINE) return WHEEL_LINE_PX;
  if (deltaMode === WheelEvent.DOM_DELTA_PAGE) return stageHeight;
  return 1;
}

function toStage(el: HTMLElement, e: { clientX: number; clientY: number }): Point {
  const r = el.getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top };
}

/** Pointer, wheel and double-tap input on the stage, mapped onto the zoomPan view. */
export function useZoomPan(image: Extent | undefined) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<Extent>();
  const [view, setView] = useState<ZoomView>();
  // zoomPan returns NaN for a zero extent, so no frame exists until both have a size.
  const frame = useMemo<ZoomFrame | undefined>(
    () =>
      stage && image && stage.w > 0 && stage.h > 0 && image.w > 0 && image.h > 0
        ? { stage, image, dpr: window.devicePixelRatio || 1 }
        : undefined,
    [stage, image],
  );
  const frameRef = useRef(frame);
  frameRef.current = frame;
  const prevFrame = useRef<ZoomFrame | undefined>(undefined);

  useEffect(() => {
    const prev = prevFrame.current;
    prevFrame.current = frame;
    if (!frame) return;
    setView((v) => {
      if (!v || !prev) return fitView(frame);
      if (prev.image !== frame.image) return swapImage(prev.image, frame, v);
      return clamp(frame, v);
    });
  }, [frame]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = (fn: (f: ZoomFrame, v: ZoomView) => ZoomView) => {
      const f = frameRef.current;
      if (f) setView((v) => (v ? fn(f, v) : v));
    };
    const pointers = new Map<number, { at: Point; down: Point }>();
    let lastTap: { t: number; p: Point } | null = null;
    let tapToggledAt = Number.NEGATIVE_INFINITY;

    const measure = () => {
      const r = el.getBoundingClientRect();
      setStage({ w: r.width, h: r.height });
    };
    const onDown = (e: PointerEvent) => {
      el.setPointerCapture?.(e.pointerId);
      const p = toStage(el, e);
      pointers.set(e.pointerId, { at: p, down: p });
    };
    const onMove = (e: PointerEvent) => {
      const mine = pointers.get(e.pointerId);
      if (!mine) return;
      const prev = mine.at;
      const next = toStage(el, e);
      mine.at = next;
      const others = [...pointers].filter(([id]) => id !== e.pointerId).map(([, s]) => s.at);
      if (others.length === 0) update((f, v) => pan(f, v, next.x - prev.x, next.y - prev.y));
      else if (others.length === 1) {
        const other = others[0];
        update((f, v) => pinch(f, v, [prev, other], [next, other]));
      }
    };
    const onUp = (e: PointerEvent) => {
      const mine = pointers.get(e.pointerId);
      pointers.delete(e.pointerId);
      if (!mine || e.pointerType !== 'touch' || pointers.size > 0) return;
      const p = toStage(el, e);
      if (Math.hypot(p.x - mine.down.x, p.y - mine.down.y) > DOUBLE_TAP_PX) {
        lastTap = null;
        return;
      }
      const last = lastTap;
      if (
        last &&
        e.timeStamp - last.t <= DOUBLE_TAP_MS &&
        Math.hypot(p.x - last.p.x, p.y - last.p.y) <= DOUBLE_TAP_PX
      ) {
        lastTap = null;
        tapToggledAt = e.timeStamp;
        update((f, v) => toggle(f, v, p));
      } else {
        lastTap = { t: e.timeStamp, p };
      }
    };
    const onCancel = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
    };
    const onDoubleClick = (e: MouseEvent) => {
      if (e.timeStamp - tapToggledAt < DBLCLICK_AFTER_TAP_MS) return;
      const p = toStage(el, e);
      update((f, v) => toggle(f, v, p));
    };
    // Also trackpad pinch, which browsers send as ctrl+wheel.
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const p = toStage(el, e);
      const px = e.deltaY * wheelUnitPx(e.deltaMode, el.getBoundingClientRect().height);
      update((f, v) => zoomAt(f, v, p, Math.exp(-px * WHEEL_RATE)));
    };

    measure();
    window.addEventListener('resize', measure);
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onCancel);
    el.addEventListener('dblclick', onDoubleClick);
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('resize', measure);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      el.removeEventListener('dblclick', onDoubleClick);
      el.removeEventListener('wheel', onWheel);
    };
  }, []);

  return { stageRef, view, percent: frame && view ? zoomPercent(frame, view) : undefined };
}
